import { useState } from "react";
import { Constants } from "constants/Constants";
import {
  GoogleMap,
  MarkerF as Marker,
  InfoWindowF as InfoWindow,
  useJsApiLoader,
  type Libraries,
} from "@react-google-maps/api";

const libraries: Libraries = ["geometry", "drawing"];

type CrudMapViewProps<T> = {
  data: T[];
  getMarkerPosition?: (item: T) => { lat: number; lng: number } | null;
  getMarkerLabel?: (item: T) => string;
  onMarkerClick?: (item: T) => void;
};

type MarkerGroup<T> = {
  position: { lat: number; lng: number };
  items: { item: T; label: string }[];
};

// Côte d'Ivoire
const defaultCenter = { lat: 7.54, lng: -5.55 };
const defaultZoom = 7;

function makeMarkerIcon(count: number): { url: string; scaledSize: any; anchor: any } {
  const W = 40;
  const H = 52;
  // Pin body : teardrop centré sur (20, 18), pointe en bas à (20, 51)
  const pinPath = "M20,1 C10,1 2,9 2,19 C2,32 20,51 20,51 C20,51 38,32 38,19 C38,9 30,1 20,1Z";
  const label = count > 1 ? String(count) : "";
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${W}" height="${H}" viewBox="0 0 40 52">
    <path d="${pinPath}" fill="#68A78E" stroke="#45725D" stroke-width="1"/>
    <circle cx="20" cy="18" r="11" fill="white"/>
    ${label ? `<text x="20" y="18" text-anchor="middle" dominant-baseline="central"
      font-family="sans-serif" font-size="${count > 9 ? 9 : 11}" font-weight="bold" fill="#22523C">${label}</text>` : ""}
  </svg>`;
  return {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    scaledSize: { width: W, height: H } as any,
    anchor: { x: W / 2, y: H } as any,
  };
}

function CrudMapView<T extends Record<string, any>>({
  data,
  getMarkerPosition,
  getMarkerLabel,
  onMarkerClick,
}: CrudMapViewProps<T>) {
  const [selectedGroupKey, setSelectedGroupKey] = useState<string | null>(null);

  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: Constants.GOOGLE_MAPS_API_KEY,
    libraries,
    version: "3.55",
  });

  // Grouper les items par position (lat,lng)
  const groups = data.reduce<Record<string, MarkerGroup<T>>>((acc, item) => {
    const position = getMarkerPosition?.(item) ?? null;
    if (!position) return acc;
    const key = `${position.lat},${position.lng}`;
    if (!acc[key]) acc[key] = { position, items: [] };
    acc[key].items.push({ item, label: getMarkerLabel?.(item) ?? "" });
    return acc;
  }, {});

  const groupEntries = Object.entries(groups);
  const center = groupEntries.length > 0 ? groupEntries[0][1].position : defaultCenter;
  const zoom = groupEntries.length === 0 ? defaultZoom : groupEntries.length === 1 ? 12 : 6;

  const selectedGroup = selectedGroupKey ? groups[selectedGroupKey] ?? null : null;

  if (!isLoaded) {
    return (
      <div className="flex items-center justify-center h-[500px] text-slate-400">
        Chargement de la carte...
      </div>
    );
  }

  return (
    <div className="w-full rounded-lg overflow-hidden border border-gray-200">
      <GoogleMap
        mapContainerStyle={{ width: "100%", height: "600px" }}
        center={center}
        zoom={zoom}
        options={{
          streetViewControl: false,
          mapTypeControl: false,
          fullscreenControl: true,
          zoomControl: true,
        }}
      >
        {groupEntries.map(([key, group]) => (
          <Marker
            key={key}
            position={group.position}
            onClick={() => setSelectedGroupKey(key)}
            icon={makeMarkerIcon(group.items.length)}
            label=""
          />
        ))}

        {selectedGroup && (
          <InfoWindow
            position={selectedGroup.position}
            onCloseClick={() => setSelectedGroupKey(null)}
          >
            <div className="p-2 max-w-xs">
              {selectedGroup.items.length > 1 && (
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">
                  {selectedGroup.items.length} parcelles
                </p>
              )}
              <ul className="flex flex-col gap-1">
                {selectedGroup.items.map(({ item, label }) => (
                  <li key={item.id} className="flex items-center gap-2">
                    <span className="text-sm font-bold text-gray-800 flex-1">{label}</span>
                    {onMarkerClick && (
                      <button
                        className="text-xs text-primary underline shrink-0"
                        onClick={() => {
                          onMarkerClick(item.id);
                          setSelectedGroupKey(null);
                        }}
                      >
                        Voir
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </InfoWindow>
        )}
      </GoogleMap>
    </div>
  );
}

export default CrudMapView;
